import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import Header from "./Header";
import CategoryChipRail from "./CategoryChipRail";
import BottomNav from "./BottomNav";
import AudioBar from "./AudioBar";
import BackgroundMusicPlayer from "./BackgroundMusicPlayer";
import SingletonMiniPlayer from "./SingletonMiniPlayer";
import ScrollUpDownFab from "./ScrollUpDownFab";
import WhatsNewToast from "./WhatsNewToast";
import Walkthrough from "./Walkthrough";
import { APP_VERSION } from "../lib/appVersion";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import GlobalPullDownToResume from "./GlobalPullDownToResume";
import PullToRefresh from "./PullToRefresh";
import AskAI from "./AskAI";
import { useAudio } from "../context/AudioContext";

// Routes that render in an immersive, full-screen layout — no app
// header, no category rail, no bottom-nav, no footer, no floating
// shortcut rails. The video itself fills the entire viewport. This is
// triggered for the live broadcast viewer/host screen so users feel
// like they're inside a native video app instead of a website with
// chrome stacked around the player.
// /discover is the Shorts-style Trending feed — it plays pinned moments
// full-screen with its own in-feed Home/mute controls, so it gets the
// same immersive (no-chrome) treatment as the live viewer.
const IMMERSIVE_ROUTE_PREFIXES = ["/live/", "/discover"];

// Languages that read right-to-left. When one of these is the active
// language we set `dir="rtl"` on the document root so every page
// (Bible chapters, recipes, sermons, devotionals, UI cards, modals,
// chat rooms — literally everything) flips its reading direction
// automatically without each page needing its own RTL handling.
const RTL_LANGUAGES = new Set(["he", "ar", "fa", "ur"]);

export function useIsImmersiveRoute() {
  const { pathname } = useLocation();
  return IMMERSIVE_ROUTE_PREFIXES.some((p) => pathname.startsWith(p));
}

// Sync `<html dir>` with the active language. We do this on
// `<html>` (not on a nested element) so portal-rendered components
// like toasts, dropdown menus, and modals inherit the right
// direction even though they mount outside Layout's subtree.
function useDocumentDirection() {
  const { language } = useAudio();
  useEffect(() => {
    if (typeof document === "undefined") return;
    const rtl = RTL_LANGUAGES.has((language || "").split("-")[0]);
    const dir = rtl ? "rtl" : "ltr";
    document.documentElement.setAttribute("dir", dir);
    document.documentElement.setAttribute("lang", language || "en");
    return () => {
      // Reset to LTR when the user signs out / app unmounts so the
      // next visitor doesn't get a stale RTL document.
      document.documentElement.setAttribute("dir", "ltr");
    };
  }, [language]);
}

export default function Layout({ children }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isStaff = user && (user.role === "owner" || user.role === "admin");
  const immersive = useIsImmersiveRoute();
  const { pathname } = useLocation();
  useDocumentDirection();

  // Floating read-aloud toolbars (AudioBar + SingletonMiniPlayer) only
  // belong on the five reader surfaces — user request (June 2026):
  // "We only need these tool bars on the Bible, Apocrypha, Precepts,
  // Concordance, Dictionary." Audio keeps playing everywhere; the
  // floating controls just stay off other pages (those pages have
  // their own inline play/pause buttons).
  const isReaderPage =
    /^\/(bible|apocrypha)(\/|$)/.test(pathname) ||
    ["/precepts", "/concordance", "/dictionary"].includes(pathname);

  if (immersive) {
    // Bare-bones shell: just the children fill the viewport. No
    // header/rails/footer chrome. The live page itself is responsible
    // for its own floating exit/back affordance.
    return (
      <div
        className="min-h-screen bg-black text-cream"
        data-testid="immersive-layout"
      >
        {children}
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-cream grain">
      <Header />
      <CategoryChipRail />
      {/* Mobile gets extra bottom padding so the new BottomNav (64 px
          + iOS safe-area) and a possibly-stacked AudioBar never cover
          page-level buttons or the last line of reading content. The
          Bible Reader specifically complained on Feb 26 that verses
          were ending up under the toolbar — bumping the floor here
          gives every page a guaranteed 144 px clear runway. */}
      <main
        className="flex-1 pb-36 md:pb-32"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 9rem)" }}
      >
        {/* Keyed wrapper gives every route a gentle opacity fade-in on
            navigation (see .hw-page-enter). Opacity-only so it never
            breaks sticky toolbars / fixed in-page players. */}
        <div key={pathname} className="hw-page-enter">
          {children}
        </div>
      </main>
      <BottomNav />
      <AskAI />
      {isReaderPage && <AudioBar />}
      <BackgroundMusicPlayer />
      {isReaderPage && <SingletonMiniPlayer />}
      <ScrollUpDownFab />
      <WhatsNewToast />
      <Walkthrough />
      <GlobalPullDownToResume />
      <PullToRefresh />
      <footer className="border-t border-cream-300/70 py-8 px-6 md:px-12 text-xs tracking-[0.15em] uppercase text-muted_ink">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-3">
          <span data-testid="footer-brand">{t("footer.brand")}</span>
          <div className="flex flex-wrap items-center gap-4">
            <Link to="/about" data-testid="footer-about-link" className="hover:text-forest">{t("footer.about", "About Us")}</Link>
            <Link to="/articles" data-testid="footer-articles-link" className="hover:text-forest">Articles</Link>
            <Link to="/faq" data-testid="footer-faq-link" className="hover:text-forest">FAQ</Link>
            <Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-forest">{t("footer.privacy")}</Link>
            <Link to="/terms" data-testid="footer-terms-link" className="hover:text-forest">{t("footer.terms")}</Link>
            {isStaff && (
              <Link to="/admin/analytics" data-testid="footer-admin-analytics-link" className="text-terracotta hover:text-forest font-bold">Admin Analytics</Link>
            )}
            <Link to="/acceptable-use" data-testid="footer-aup-link" className="hover:text-forest">Acceptable Use</Link>
            <Link to="/settings" data-testid="footer-settings-link" className="hover:text-forest">{t("footer.settings")}</Link>
            <span>KJV · ASV · WEB</span>
            {/* App version chip — visible footprint so closed-test
                reviewers (and the owner) can confirm at a glance which
                build a phone is running. Bumped per AAB release. */}
            <span
              data-testid="footer-app-version"
              className="px-2 py-0.5 text-forest hover:text-terracotta tracking-[0.18em] text-[10px]"
            >
              v{APP_VERSION}
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
}
